import { useCallback, useState } from "react";
import { useAction } from "convex/react";
import { api } from "@constructionautomation/backend";
import { toast } from "sonner";
import { notifyError } from "@/lib/notify-error";
import { usePageRestore } from "@/lib/use-page-restore";

/** Stable server error contract from billing/errors.ts; `message` is safe to show. */
function getBillingErrorData(error: unknown) {
  if (typeof error === "object" && error !== null && "data" in error) {
    const data = error.data;
    if (typeof data === "object" && data !== null && "code" in data && "message" in data &&
        typeof data.code === "string" && data.code.startsWith("BILLING_") && typeof data.message === "string") {
      return { code: data.code, message: data.message };
    }
  }
  return null;
}

export function getBillingErrorMessage(error: unknown, fallback: string) {
  return getBillingErrorData(error)?.message ?? fallback;
}

/** Provider or network failures may succeed on retry; other billing errors are final answers. */
export function isRetryableBillingError(error: unknown) {
  const data = getBillingErrorData(error);
  return data === null || data.code === "BILLING_PROVIDER_ERROR";
}

export type CheckoutReturn =
  | { kind: "success"; sessionId: string }
  | { kind: "canceled" };

/** The Checkout return the server encoded in the success/cancel URLs, if this page load is one. */
export function readCheckoutReturn(search: string): CheckoutReturn | null {
  const params = new URLSearchParams(search);
  const checkout = params.get("checkout");
  const sessionId = params.get("session_id");
  if (checkout === "success" && sessionId) return { kind: "success", sessionId };
  if (checkout === "canceled") return { kind: "canceled" };
  return null;
}

/** Drop the Checkout return parameters so a reload doesn't confirm again. */
export function clearCheckoutReturn() {
  const url = new URL(window.location.href);
  url.searchParams.delete("checkout");
  url.searchParams.delete("session_id");
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
}

/**
 * The one way the web app opens billing: the organization billing page and the access-required
 * dialog both call this. The server decides between Checkout and the Customer Portal and refuses
 * a second purchase while one is pending. `newTab` keeps the current page (and unsaved work) open;
 * the tab is opened during the click so popup blockers allow it, then pointed at Stripe.
 */
export function useBillingSession(options: { returnPath?: string; newTab?: boolean } = {}) {
  const openBillingSession = useAction(api.billing.selfServe.openBillingSession);
  const [pendingIntent, setPendingIntent] = useState<"auto" | "manage" | null>(null);
  // A same-tab redirect leaves `pendingIntent` set; Back from Stripe restores it from bfcache.
  usePageRestore(() => setPendingIntent(null));

  const open = useCallback(async (intent: "auto" | "manage" = "auto") => {
    setPendingIntent(intent);
    const tab = options.newTab ? window.open("", "_blank") : null;
    if (tab) tab.opener = null;
    try {
      const result = await openBillingSession({
        returnOrigin: window.location.origin,
        returnPath: options.returnPath,
        intent,
      });
      if (result.action === "checkout" || result.action === "portal") {
        if (tab) {
          tab.location.href = result.url;
          setPendingIntent(null);
        } else {
          window.location.assign(result.url);
        }
        return result;
      }
      tab?.close();
      if (result.action === "pending") {
        toast.info("Your payment is still being confirmed. Access starts as soon as it is.");
      } else {
        toast.success("Your subscription is active.");
      }
      setPendingIntent(null);
      return result;
    } catch (error) {
      tab?.close();
      console.error("Open billing error:", error);
      notifyError(error, getBillingErrorMessage(error, "Billing could not be opened. Try again in a moment."));
      setPendingIntent(null);
      return null;
    }
  }, [openBillingSession, options.returnPath, options.newTab]);

  const pendingAction = pendingIntent === "manage" ? "portal" as const : pendingIntent ? "checkout" as const : null;
  return { open, pendingIntent, pendingAction };
}
